import { useState, useEffect, useRef } from "react";
import { Mail, Lock, User, Eye, EyeOff, MapPin } from "lucide-react";
import { useGoogleLogin } from "@react-oauth/google";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/services/api";
import { getOrCreateVisitorId } from "@/services/geo";
import { subscribeReadySW } from "@/services/push";
import { Bell } from "lucide-react";

const POSITIONS = [
  { value: "Gardien", short: "G", label: "Gardien" },
  { value: "Defenseur", short: "D", label: "Defenseur" },
  { value: "Milieu", short: "M", label: "Milieu" },
  { value: "Attaquant", short: "A", label: "Attaquant" },
];

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    position: "",
    city: "",
  });
  const [cityCoords, setCityCoords] = useState(null); // {lat,lng} once validated
  const [cityHits, setCityHits] = useState([]);
  const [notifyAlerts, setNotifyAlerts] = useState(true);
  const cityDebounceRef = useRef(null);
  const { login, register, setSession } = useAuth();

  // Direct Google OAuth (Google Identity Services, auth-code popup flow).
  // Opens the native Google account chooser ON THE APP DOMAIN — no intermediate page.
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (resp) => {
      try {
        const data = await api.googleAuth(resp.code);
        setSession(data.token, data.user);
        toast.success("Connexion Google reussie");
      } catch (err) {
        toast.error(err.message || "Echec de la connexion Google");
      }
    },
    onError: () => toast.error("Connexion Google annulee"),
  });

  // Track anonymous visitors (idempotent per browser via UUID in localStorage)
  useEffect(() => {
    const vid = getOrCreateVisitorId();
    if (vid) {
      api.trackAnonymousVisit(vid).catch(() => {});
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "city") {
      setCityCoords(null);
      if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
      if (!value.trim()) {
        setCityHits([]);
        return;
      }
      cityDebounceRef.current = setTimeout(async () => {
        try {
          const res = await api.geoForward(value);
          if (res?.lat != null && res?.lng != null) {
            setCityHits([{ city: res.city, lat: res.lat, lng: res.lng }]);
          } else {
            setCityHits([]);
          }
        } catch {
          setCityHits([]);
        }
      }, 350);
    }
  };

  const handlePickCity = (hit) => {
    setFormData((prev) => ({ ...prev, city: hit.city }));
    setCityCoords({ lat: hit.lat, lng: hit.lng });
    setCityHits([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // MOBILE: request the native permission as the VERY FIRST action of the click
    // gesture (before any other logic/await) so iOS/Android don't drop the prompt.
    let pushGranted = false;
    if (!isLogin && notifyAlerts && typeof Notification !== "undefined" && "serviceWorker" in navigator) {
      try {
        const permission = await Notification.requestPermission();
        pushGranted = permission === "granted";
      } catch (err) {
        console.error("requestPermission failed", err);
      }
    }

    if (!formData.email || !formData.password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    if (!isLogin && !formData.name) {
      toast.error("Veuillez entrer votre nom");
      return;
    }

    setIsLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Connexion reussie!");
      } else {
        await register(
          formData.name, formData.email, formData.password,
          formData.position, formData.city,
          cityCoords?.lat ?? null, cityCoords?.lng ?? null,
        );
        toast.success("Compte cree avec succes!");
        // Permission already granted above → subscribe via the ready SW after auth.
        if (pushGranted) {
          subscribeReadySW().then((ok) => {
            if (ok) toast.success("Alertes matchs activees !");
          }).catch((err) => console.error("subscribe after signup failed", err));
        }
      }
    } catch (error) {
      toast.error(error.message || "Une erreur est survenue");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-black flex flex-col items-center justify-center px-4">
      {/* Logo */}
      <div className="text-center mb-10">
        <h1 className="font-sport uppercase text-white text-5xl tracking-tight mb-2">MatchUp</h1>
        <p className="text-gray-500 text-base">Trouve ton prochain match</p>
      </div>

      {/* Form */}
      <div className="w-full max-w-sm">
        <div className="bg-[#111111] border border-[#333] rounded-2xl p-6">
          {/* Toggle */}
          <div className="flex mb-6 bg-black rounded-xl p-1">
            <button type="button" onClick={() => setIsLogin(true)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${isLogin ? "bg-[#22c55e] text-white" : "text-gray-500 hover:text-white"}`}>
              Connexion
            </button>
            <button type="button" onClick={() => setIsLogin(false)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${!isLogin ? "bg-[#22c55e] text-white" : "text-gray-500 hover:text-white"}`}>
              Inscription
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-gray-400">Nom complet</Label>
                <div className="relative">
                  <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                  <Input data-testid="register-name-input" id="name" name="name" type="text"
                    value={formData.name} onChange={handleChange} placeholder="Thomas Martin"
                    className="h-12 pl-11 w-full rounded-xl bg-black border-[#333] text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#22c55e] focus:border-transparent" />
                </div>
              </div>
            )}

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-400">Email</Label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input data-testid="login-email-input" id="email" name="email" type="email"
                  value={formData.email} onChange={handleChange} placeholder="ton@email.com"
                  className="h-12 pl-11 w-full rounded-xl bg-black border-[#333] text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#22c55e] focus:border-transparent" />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-400">Mot de passe</Label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input data-testid="login-password-input" id="password" name="password"
                  type={showPassword ? "text" : "password"} value={formData.password} onChange={handleChange}
                  placeholder="••••••••"
                  className="h-12 pl-11 pr-11 w-full rounded-xl bg-black border-[#333] text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#22c55e] focus:border-transparent" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Position & City (Registration only) */}
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="position" className="text-sm font-medium text-gray-400">Poste prefere</Label>
                  <div data-testid="position-chips" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    {POSITIONS.map(p => {
                      const selected = formData.position === p.value;
                      return (
                        <button
                          key={p.value}
                          type="button"
                          data-testid={`position-chip-${p.short}`}
                          onClick={() => setFormData(prev => ({ ...prev, position: p.value }))}
                          style={{
                            minHeight: '64px',
                            borderRadius: '12px',
                            backgroundColor: selected ? 'rgba(34,197,94,0.12)' : '#000',
                            border: selected ? '2px solid #22c55e' : '1px solid #333',
                            color: selected ? '#22c55e' : '#9ca3af',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.15s ease',
                            fontFamily: 'inherit',
                            touchAction: 'manipulation',
                            boxShadow: selected ? '0 0 12px rgba(34,197,94,0.35)' : 'none',
                          }}
                        >
                          <span style={{ fontSize: '22px', fontWeight: 800, lineHeight: 1 }}>{p.short}</span>
                          <span style={{ fontSize: '10px', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{p.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city" className="text-sm font-medium text-gray-400">Ville</Label>
                  <div className="relative">
                    <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                    <Input data-testid="register-city-input" id="city" name="city" type="text"
                      value={formData.city} onChange={handleChange}
                      autoComplete="off"
                      placeholder="Paris, Valenciennes, Lyon..."
                      className="h-12 pl-11 pr-20 w-full rounded-xl bg-black border-[#333] text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#22c55e] focus:border-transparent" />
                    {cityCoords && (
                      <span data-testid="city-coords-ok" style={{
                        position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                        fontSize: 10, fontWeight: 700, color: "#22c55e",
                        background: "rgba(34,197,94,0.12)", padding: "3px 8px",
                        borderRadius: 999, border: "1px solid rgba(34,197,94,0.35)",
                      }}>GPS OK</span>
                    )}
                    {!cityCoords && cityHits.length > 0 && (
                      <ul data-testid="city-suggestions" style={{
                        position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                        background: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: 12,
                        listStyle: "none", padding: 4, margin: 0, zIndex: 1000,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
                      }}>
                        {cityHits.map((h) => (
                          <li
                            key={h.city + h.lat}
                            onMouseDown={(e) => { e.preventDefault(); handlePickCity(h); }}
                            style={{
                              padding: "10px 12px", borderRadius: 8, color: "#fff",
                              fontSize: 13, cursor: "pointer", display: "flex",
                              alignItems: "center", gap: 8,
                            }}
                            className="hover:bg-[#1a1a1a]"
                          >
                            <MapPin size={13} style={{ color: "#22c55e", flexShrink: 0 }} />
                            <span style={{ flex: 1 }}>{h.city}</span>
                            <span style={{ fontSize: 10, color: "#6b7280" }}>
                              {h.lat.toFixed(2)}, {h.lng.toFixed(2)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {formData.city && !cityCoords && cityHits.length === 0 && (
                    <p style={{ fontSize: 11, color: "#f59e0b", marginTop: 4 }}>
                      Continue de taper pour valider ta ville (GPS necessaire pour recevoir les notifications)
                    </p>
                  )}
                </div>
              </>
            )}

            {!isLogin && (
              <label
                data-testid="notify-alerts-checkbox"
                className="flex items-center gap-3 cursor-pointer select-none"
                style={{ padding: "12px 14px", borderRadius: 12, background: "#0a0a0a", border: "1px solid #1f1f1f" }}
              >
                <input
                  type="checkbox"
                  checked={notifyAlerts}
                  onChange={(e) => setNotifyAlerts(e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: "#22c55e", flexShrink: 0 }}
                />
                <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#d1d5db" }}>
                  <Bell size={15} color="#22c55e" />
                  Recevoir les alertes de mes matchs
                </span>
              </label>
            )}

            {/* Submit */}
            <Button data-testid="login-submit-button" type="submit" disabled={isLoading}
              className="w-full h-12 mt-6 rounded-xl bg-[#22c55e] hover:bg-[#16a34a] text-white font-semibold text-base transition-all active:scale-[0.98] shadow-lg shadow-[#22c55e]/20 disabled:opacity-50">
              {isLoading ? "Chargement..." : (isLogin ? "Se connecter" : "Creer mon compte")}
            </Button>
          </form>

          {/* Google Login - Direct Google OAuth (Google Identity Services popup) */}
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <div className="mt-5">
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-800" /></div>
              <div className="relative flex justify-center text-[11px]"><span className="px-3 bg-black text-gray-500 uppercase tracking-wider">ou</span></div>
            </div>
            <button
              type="button"
              data-testid="google-login-btn"
              onClick={() => googleLogin()}
              style={{
                width: "100%", minHeight: "48px", borderRadius: "12px",
                backgroundColor: "#fff", color: "#1f2937",
                fontSize: "14px", fontWeight: 700, fontFamily: "inherit",
                border: "1px solid #e5e7eb", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
                touchAction: "manipulation",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Continuer avec Google
            </button>
          </div>
        </div>
      </div>

      <p className="text-gray-600 text-xs mt-8">En continuant, tu acceptes nos conditions d'utilisation</p>
    </div>
  );
}
